<template>
  <NuxtLayout>
    <VitePwaManifest />
    <NuxtPage />

    <ClientOnly>
      <SingleButtonDialog
        :custom-class="dialog.customClass"
        :dialog-visible-trigger="dialog.visible"
        :dialog-title="dialog.title"
        :dialog-description="dialog.description"
        :single-text="dialog.buttonText"
        @click-single-button="confirm"
        @close-dialog="close"
      />
    </ClientOnly>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { ID_INJECTION_KEY } from 'element-plus'
import { useSingleButtonDialog } from './uComposables/modalControl/useSingleButtonDialog'
const { userData, pending } = storeToRefs(useUserData())
const { locale } = useLocale()
const { isApp } = useAppCheck()
const { dialog, confirm, close } = useSingleButtonDialog()
provide(ID_INJECTION_KEY, {
  prefix: 100,
  current: 0
})

const pageTitle = ref('')

useHead({
  htmlAttrs: {
    lang: locale
  },
  titleTemplate: (title?: string) => {
    pageTitle.value = title ?? ''
    return title ?? ''
  },
  meta: () => [{ hid: 'title', name: 'title', content: pageTitle.value }]
})

const { initMkt, trackPageView, trackUser, trackLogout } = useMarketSDK()

onMounted(() => {
  useChannelTalk().startChannelTalk()
  initMkt()
  trackPageView()
})

// 로그인/로그아웃 전환 상태 저장
const lastAuthState = ref<'unknown' | 'loggedIn' | 'loggedOut'>('unknown')

// 이전 쿠폰 상태 스냅샷 저장
let lastCouponsSnapshot: string | null = null

watch(
  () => ({
    pending: pending.value,
    hasUser: !!userData.value,
    coupons: userData.value?.userCoupons ?? []
  }),
  ({ pending, hasUser, coupons }) => {
    if (pending) return

    const snapshot = JSON.stringify(coupons)

    // 1) 로그인 전환
    if (hasUser && lastAuthState.value !== 'loggedIn') {
      if (import.meta.client) trackUser(userData.value!)
      lastAuthState.value = 'loggedIn'
      lastCouponsSnapshot = snapshot // 첫 스냅샷 저장
      return
    }

    // 2) 로그아웃 전환
    if (!hasUser && lastAuthState.value !== 'loggedOut') {
      if (import.meta.client) trackLogout()
      lastAuthState.value = 'loggedOut'
      lastCouponsSnapshot = null // 스냅샷 초기화
      return
    }

    // 3) 로그인 유지 중 쿠폰 데이터 변화
    if (hasUser && snapshot !== lastCouponsSnapshot) {
      if (import.meta.client) trackUser(userData.value!)
      lastCouponsSnapshot = snapshot
    }
  },
  { immediate: true, deep: true }
)

const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    trackPageView()
  }
)

// const { url } = useBlob()
// const naverProducts = [
//   {
//     name: '일본 데이터',
//     imagePath: '/naver-SA/JP.jpg',
//     productId: '179D30E5-6A69-EE11-BBF0-28187860D6D3'
//   },
//   {
//     name: '중국 데이터',
//     imagePath: '/naver-SA/CN.jpg',
//     productId: '1B9D30E5-6A69-EE11-BBF0-28187860D6D3'
//   },
//   {
//     name: '필리핀 데이터',
//     imagePath: '/naver-SA/PH.jpg',
//     productId: '1F9D30E5-6A69-EE11-BBF0-28187860D6D3'
//   },
//   {
//     name: '태국 데이터',
//     imagePath: '/naver-SA/TH.jpg',
//     productId: '199D30E5-6A69-EE11-BBF0-28187860D6D3'
//   },
//   {
//     name: '베트남 데이터',
//     imagePath: '/naver-SA/VN.jpg',
//     productId: '1A9D30E5-6A69-EE11-BBF0-28187860D6D3'
//   }
// ]

// const naverSAList = {
//   '@context': 'https://schema.org',
//   '@type': 'ItemList',
//   itemListElement: naverProducts.map((product, index) => ({
//     '@type': 'ListItem',
//     position: index + 1,
//     item: {
//       '@type': 'Product',
//       name: product.name,
//       image: url(product.imagePath),
//       url: `https://www.usimsa.com/product?productId=${product.productId}`
//     }
//   }))
// }

// useJsonld(naverSAList)

useJsonld({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'USIMSA',
  url: 'https://www.usimsa.com',
  sameAs: [
    'https://blog.naver.com/usimsa_official',
    'https://kin.naver.com/profile/index.nhn?u=UMS5jTa95aiXI94Fd8OPBgXkWaVcWjaJu1epkJ5d5s8%3D',
    'https://www.facebook.com/checkpoint/828281030927956/?next=https://www.facebook.com/profile.php?id%3D100066896256325%26sk%3Dabout',
    'https://www.instagram.com/usimsa.official',
    'https://www.youtube.com/@usimsa'
  ]
})

onMounted(() => {
  if (isApp) {
    document.documentElement.classList.add('is-app')
  }
})
</script>
<style>
html {
  scrollbar-gutter: stable;
}
</style>
